import { useMemo } from "react";
import ActionWrapper from "Common/DataTable/ActionWrapper";
import EditBtn from "Common/DataTable/EditBtn";
import DeleteBtn from "Common/DataTable/DeleteBtn";
import PrintBtn from "Common/DataTable/PrintBtn";
import { Link } from "react-router-dom";
import { getImageUrl } from "utils/imageHelper";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import { GetLastThreeUrl } from "utils/CommonUtils";
import { useRoleVerify } from "Common/RoleVerify";

const useTableLoanReportConfig = ({
    data = [],
    page = 1,
    limit = 10,
    canShowAction = false,
    refetch = () => { },
    config = {},
    IsModel = false,
    IsEdit = true,
    IsDelete = true,
    isView = false,
    isPrint = false,
    IsEditDisble = false,
    IsEditDisbleKey = ''
}) => {
    const { entityName, editUrl, idKey, displayFields, deleteApi, ViewUrl = "", PrintApi, poolPrintKeys } = config;

    const formattedData = useMemo(() => {
        return data.map((item, index) => {
            const row = { rowNo: (page - 1) * limit + index + 1 };
            displayFields.forEach((field) => {
                row[field.accessor] = item[field.dataKey];
            });
            if (IsEditDisble) row[IsEditDisbleKey] = item[IsEditDisbleKey];
            row[idKey] = item[idKey];
            return row;
        });
    }, [
        IsEditDisble,
        IsEditDisbleKey,
        data,
        page,
        limit,
        displayFields,
        idKey
    ]
    );

    const ViewBtn = ({ url }) => {
        const page_url = GetLastThreeUrl();

        if (!useRoleVerify("", ["can_access"])) return null;

        const [first, _] = page_url.split("/")
        return (
            <Link
                to={`/${first}/${url}`}
                className="btn btn-accent btn-sm border rounded-2 me-1"
                aria-label="View"
                title="View"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className="svg-stroke">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                    <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
                </svg>
            </Link>
        );
    };

    const columns = useMemo(() => {
        const baseColumns = [
            { Header: "No", accessor: "rowNo" },
            ...displayFields.map(({ Header, accessor, isImage }) => ({
                Header,
                accessor,
                Cell: ({ value, column }) => {
                    if (!value) return null;

                    // Only process as image/doc if specifically marked as isImage
                    if (isImage) {
                        const isString = typeof value === "string";
                        const isDoc = isString && (
                            value.toLowerCase().endsWith(".pdf") ||
                            value.toLowerCase().endsWith(".doc") ||
                            value.toLowerCase().endsWith(".docx")
                        );

                        if (isDoc) {
                            return (
                                <a href={getImageUrl(value)} target="_blank" rel="noreferrer" className="text-decoration-none">
                                    <i className={`bi ${value.toLowerCase().endsWith(".pdf") ? "bi-filetype-pdf text-danger" : "bi-filetype-doc"} fs-2`} style={{ color: value.toLowerCase().endsWith(".pdf") ? "#dc3545" : "#103F91" }}></i>
                                </a>
                            );
                        }

                        return (
                            <PhotoProvider>
                                <PhotoView src={getImageUrl(value)}>
                                    <img
                                        src={getImageUrl(value)}
                                        className="img-fluid avatar lg rounded"
                                        alt={column.Header || "Thumbnail"}
                                        style={{ cursor: "pointer" }}
                                        onError={(e) => {
                                            e.currentTarget.src =
                                                "data:image/svg+xml;charset=utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><rect width='100%' height='100%' fill='%23eee'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-size='12' fill='%23999'>no image</text></svg>";
                                        }}
                                    />
                                </PhotoView>
                            </PhotoProvider>
                        );
                    }

                    return value;
                }
            })),
        ];

        if (!canShowAction || (!IsEdit && !IsDelete && !isView && !IsModel && !isPrint)) return baseColumns;

        return [
            ...baseColumns,
            {
                Header: "Action",
                accessor: "action",
                sortable: false,
                Cell: ({ row }) => (
                    <ActionWrapper>
                        {IsEdit && <EditBtn url={`${editUrl}/${row.original[idKey]}`} />}
                        {isView && <ViewBtn url={`${ViewUrl}/${row.original[idKey]}`} />}
                        {isPrint && (<PrintBtn
                            id={row.original[idKey]}
                            apiPrintFunction={PrintApi}
                            printkeysjson={poolPrintKeys}
                            title="Pool Booking Receipt"
                        />)
                        }
                        {IsDelete && (
                            <DeleteBtn
                                id={row.original[idKey]}
                                fetchData={refetch}
                                apiDeleteFunction={deleteApi}
                                itemName={entityName}
                            />
                        )}
                        {
                            IsModel?.show && (
                                <Link
                                    to={`${editUrl}/${row.original[idKey]}`}
                                    id={row.original[idKey]}
                                    className="btn btn-primary"
                                >
                                    {IsModel.val}
                                </Link>
                            )
                        }
                    </ActionWrapper>
                ),
            },
        ];
    }, [
        IsModel,
        isPrint,
        displayFields,
        canShowAction,
        refetch,
        editUrl,
        idKey,
        entityName,
        deleteApi,
        IsEdit,
        IsDelete,
        isView,
        ViewUrl
    ]
    );

    return { formattedData, columns };
};

export default useTableLoanReportConfig;